import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Field } from 'redux-form';

import { Button, Currency, Spinner, RenderInBody } from '../../../components';
import Select from '~COMPONENTS/formInputs/Select';
import InputLarge from '~COMPONENTS/formInputs/InputLarge';
import { TranslatedCopy } from '../../../intl/TranslatedCopy';

import { ZERO_DOLLAR_VALUE } from '../../../util/constants';
import { onlyPositiveNumber, isNotZero, required } from '~APP_UTIL/formValidations';
import { hasMoneyLeft, hasEnoughMoney, overLockedAmt } from './transferValidations';

import { newDepartmentTypes } from '../../../types/allotments';
import currencySymbols from '../../../intl/currencySymbols.json';
import { GA_EVENT_ALLOTMENTS_SAVE_ALLOTMENTS_CLICK, sendEventToGA } from '../../../analytics/constants';

const normalizeDepartmentName = name => name.toLowerCase().split(' ').join('-');

const normalizeCurrency = value => {
  if (!value) {
    return value;
  }
  if (value.split('.').length > 1 && value.split('.')[1].length > 2) {
    return parseFloat(value).toFixed(2);
  }
  return value;
};

const MakeTransfer = ({
  departments,
  unallotedFunds,
  onSubmit,
  onCancel,
  isOpen,
  handleSubmit,
  submitting,
  valid,
  allotmentForm,
  currencyCode
}) => {
  const renderDepartment = (name, allotment, currencyCode, dept) => {
    const normalizedName = typeof name === 'string' ? normalizeDepartmentName(name) : name;

    return {
      value: name,
      label: (
        <div className='d-sm-ib'>
          <div data-desc={`select__option_${normalizedName}_name`}>{dept.departmentName}</div>
          <div className='text-color-secondary body-4' data-desc={`select__option_${normalizedName}_value`}>
            <Currency value={allotment} type={currencyCode} error={allotment < ZERO_DOLLAR_VALUE} />
          </div>
        </div>
      ),
      dept: dept
    };
  };

  const mappedDepartments = departments.map(dept =>
    renderDepartment(dept.departmentName, dept.remainingAllotment, dept.currencyCode, dept)
  );

  const hasValues = allotmentForm && allotmentForm.values;

  const deptFromOptions =
    hasValues && allotmentForm.values.transferTo && !!allotmentForm.values.transferTo.value
      ? mappedDepartments.filter(dept => dept.value !== allotmentForm.values.transferTo.value)
      : mappedDepartments;

  deptFromOptions.unshift(
    renderDepartment('allotments.dashboard.unallocated', unallotedFunds, departments[0].currencyCode, {
      departmentName: new TranslatedCopy({ id: 'allotments.dashboard.unallocated' }).toString(),
      departmentNumber: 'unallocated',
      remainingAllotment: unallotedFunds,
      currencyCode: departments[0].currencyCode,
      lockedAllotment: ZERO_DOLLAR_VALUE
    })
  );

  const deptToOptions =
    hasValues && allotmentForm.values.transferFrom && !!allotmentForm.values.transferFrom.value
      ? deptFromOptions.filter(dept => dept.value !== allotmentForm.values.transferFrom.value)
      : deptFromOptions;

  return (
    <aside className='makeTransfer ta-sm-l pb2-sm'>
      <RenderInBody>
        <div className={classnames('sidebar__submit prl2-sm', { isOpen: isOpen })}>
          <Button
            secondary
            extraClassName='mr2-md ml2-md mb4-sm'
            onClick={onCancel}
            data-desc='currentAllotmentsForm__cancel'
          >
            <TranslatedCopy id='global.cancel' />
          </Button>
          <Button
            type='submit'
            onClick={() => {
              handleSubmit(onSubmit)();
              sendEventToGA(GA_EVENT_ALLOTMENTS_SAVE_ALLOTMENTS_CLICK);
            }}
            disabled={!valid || submitting}
            data-desc='currentAllotmentsForm__submit'
            extraClassName='ml10-sm mr2-md ml2-md mb4-sm'
          >
            {submitting ? <Spinner className='relative-container' /> : <TranslatedCopy id='allotments.form.save' />}
          </Button>
        </div>
      </RenderInBody>
      {isOpen && (
        <>
          <label htmlFor='allotments.form.transfer.from.label' className='headline-5 u-bold'>
            <TranslatedCopy id='allotments.form.transfer.from.label' />
          </label>
          {deptFromOptions && (
            <>
              <Field
                name='transferFrom'
                component={Select}
                validate={[required, hasMoneyLeft]}
                options={deptFromOptions}
                placeholder={<TranslatedCopy id='allotments.form.transfer.placeholder' />}
                extraClassName='body-3'
                matchPos='start'
                ignoreCase
                matchProp='any'
                required
                forceDropdown
              />
            </>
          )}

          <label htmlFor='allotments.form.transfer.to.label' className='headline-5 u-bold'>
            <TranslatedCopy id='allotments.form.transfer.to.label' />
          </label>
          {deptToOptions && (
            <Field
              name='transferTo'
              component={Select}
              validate={[required]}
              options={deptToOptions}
              placeholder={<TranslatedCopy id='allotments.form.transfer.placeholder' />}
              extraClassName='body-3'
              required
              forceDropdown
            />
          )}
          <label htmlFor='allotments.form.transfer.amt.label' className='headline-5 u-bold'>
            <TranslatedCopy id='allotments.form.transfer.amt.label' />
          </label>
          <Field
            name='transferAmt'
            type='number'
            step='0.01'
            component={InputLarge}
            placeholder='0'
            label={currencySymbols[currencyCode]}
            hasSymbol
            validate={[onlyPositiveNumber, hasEnoughMoney, overLockedAmt, isNotZero, required]}
            normalize={normalizeCurrency}
            required
          />
        </>
      )}
    </aside>
  );
};

MakeTransfer.defaultProps = {
  allotmentForm: null,
  unallotedFunds: null,
  currencyCode: 'USD'
};

MakeTransfer.propTypes = {
  allotmentForm: PropTypes.any,
  departments: PropTypes.arrayOf(newDepartmentTypes).isRequired,
  currencyCode: PropTypes.string,
  unallotedFunds: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired
};

export default MakeTransfer;
